/**
 * Centralized error message utility
 * Maps technical errors to user-friendly messages with suggested actions
 */

export type ErrorAction = 'retry' | 'upgrade' | 'support' | 'login' | 'wait' | 'none';

export interface UserFriendlyError {
  title: string;
  message: string;
  action: ErrorAction;
  variant: 'destructive' | 'default';
}

interface ErrorMapping {
  pattern: RegExp;
  error: Omit<UserFriendlyError, 'variant'> & { variant?: 'destructive' | 'default' };
}

const errorMappings: ErrorMapping[] = [
  // Content Filter (Azure false positive)
  {
    pattern: /content.?filter|content.?management.?policy|ResponsibleAIPolicyViolation/i,
    error: {
      title: 'Contact Cannot Be Extracted',
      message: 'This email signature could not be processed. Please try a different email.',
      action: 'none',
      variant: 'default',
    },
  },
  // No Active Subscription
  {
    pattern: /no active subscription|subscription not found/i,
    error: {
      title: 'No Active Subscription',
      message: 'No active subscription found. Please subscribe to a plan to continue.',
      action: 'upgrade',
    },
  },
  // Network & Connection Errors
  {
    pattern: /fetch failed|network|ECONNREFUSED|timeout|Failed to fetch/i,
    error: {
      title: 'Connection Error',
      message: 'Could not connect to the server. Please check your internet connection and try again.',
      action: 'retry',
    },
  },
  // Generic Edge Function error (fallback - should rarely match now)
  {
    pattern: /edge function|non-2xx/i,
    error: {
      title: 'Server Error',
      message: 'The server returned an error. Please try again.',
      action: 'retry',
    },
  },
  // Rate Limiting
  {
    pattern: /rate limit|429|too many requests/i,
    error: {
      title: 'Too Many Requests',
      message: 'You\'re sending requests too quickly. Please wait a moment and try again.',
      action: 'wait',
    },
  },
  // Credits & Payment
  {
    pattern: /credits exhausted|payment required|402|insufficient credits|no credits/i,
    error: {
      title: 'Insufficient Credits',
      message: 'You\'ve run out of credits. Please purchase more to continue.',
      action: 'upgrade',
    },
  },
  // Authentication
  {
    pattern: /unauthorized|401|not authenticated|session expired|invalid token|PERMISSION_REQUIRED/i,
    error: {
      title: 'Authentication Required',
      message: 'Please sign in again to continue.',
      action: 'login',
    },
  },
  // Forbidden / Access Denied
  {
    pattern: /forbidden|403|access denied|not allowed/i,
    error: {
      title: 'Access Denied',
      message: 'You don\'t have permission to perform this action.',
      action: 'support',
    },
  },
  // Content Size
  {
    pattern: /too large|payload|size limit|body size|UnableToDeserializePostBody|Deserialize/i,
    error: {
      title: 'Content Too Large',
      message: 'The email content is too large to process. Try with a shorter email.',
      action: 'none',
    },
  },
  // Subscription Required
  {
    pattern: /subscription required|active subscription|subscribe first/i,
    error: {
      title: 'Subscription Required',
      message: 'Please subscribe to a plan to use this feature.',
      action: 'upgrade',
    },
  },
  // Stripe / Checkout
  {
    pattern: /checkout|stripe|payment failed/i,
    error: {
      title: 'Payment Error',
      message: 'There was a problem processing your payment. Please try again.',
      action: 'retry',
    },
  },
  // Email/Content Not Found
  {
    pattern: /no email content|no signature|could not extract|missing required data/i,
    error: {
      title: 'No Content Found',
      message: 'Could not find email content to process. Make sure an email is open.',
      action: 'none',
      variant: 'default',
    },
  },
  // Server Errors
  {
    pattern: /500|internal server|server error/i,
    error: {
      title: 'Server Error',
      message: 'Something went wrong on our end. Please try again in a moment.',
      action: 'retry',
    },
  },
];

/**
 * Maps a technical error to a user-friendly message
 */
export function getErrorMessage(error: Error | string | unknown, context?: string): UserFriendlyError {
  let errorString: string;
  
  if (error instanceof Error) {
    errorString = error.message;
  } else if (typeof error === 'string') {
    errorString = error;
  } else if (error && typeof error === 'object' && 'message' in error) {
    errorString = String((error as { message: unknown }).message);
  } else {
    errorString = String(error);
  }

  // Find matching error pattern
  for (const mapping of errorMappings) {
    if (mapping.pattern.test(errorString)) {
      return {
        title: mapping.error.title,
        message: mapping.error.message,
        action: mapping.error.action,
        variant: mapping.error.variant || 'destructive',
      };
    }
  }

  // Default fallback
  return {
    title: 'Something Went Wrong',
    message: context 
      ? `${context}. Please try again or contact support if the problem persists.`
      : 'An unexpected error occurred. Please try again or contact support.',
    action: 'support',
    variant: 'destructive',
  };
}

/**
 * Checks if an error is recoverable (can be retried)
 */
export function isRecoverableError(error: Error | string | unknown): boolean {
  const mapped = getErrorMessage(error);
  return mapped.action === 'retry' || mapped.action === 'wait';
}

/**
 * Gets the recommended wait time for rate limit errors (in ms)
 */
export function getRetryDelay(error: Error | string | unknown): number {
  const errorString = error instanceof Error ? error.message : String(error);
  
  if (/rate limit|429/i.test(errorString)) {
    return 30000; // 30 seconds for rate limits
  }
  
  if (/timeout|network/i.test(errorString)) {
    return 5000; // 5 seconds for network issues
  }
  
  return 3000; // Default 3 seconds
}
